import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutMultiGen } from '../layoutMulti';
import { OptsMulti } from '../optsMulti';
import { GUtil } from '../../pdf/gutil';
import { genMulti, calcOrigin } from '../../core/genHelper';
import { C } from '../../core/c';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';

/**
 * Dlazdice (Tile Puzzle) Generator
 *
 * Creates a tile-based puzzle where students solve equations
 * to find the correct direction to the next tile.
 * The path snakes through a shape defined by a 2D matrix.
 *
 * Migrated from C# EduFrame.Multi.Generators.Dlazdice
 */

interface PathStep {
  a: C;
  b: C;
}

interface PathResult {
  width: number;
  height: number;
  cards: Map<string, number>;
  path: PathStep[];
  solution: number[][];
}

/**
 * Abstract base class for tile puzzle generators.
 * Subclasses define different shapes via getMatrix().
 */
export abstract class DlazdiceBase implements ILayoutMultiGen, IItemCount {
  /** Returns 2D matrix where 1 = tile cell, 0 = empty */
  abstract getMatrix(): number[][];

  countDefault(): number {
    return 1;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const margin1 = PdfUnit.FromMillimeters(3);
    const margin2 = PdfUnit.FromMillimeters(1);

    const result = this.getPath(opts);

    const {lang} = opts.data;
    const startLabel = lang.Generator_Start ?? 'START';

    const c = { x: 3, y: 4 }; // Grid for question cards
    const sz = Math.min(
      (PdfSize.A4.width - 2 * margin) / c.x,
      (PdfSize.A4.height - 2 * margin) / c.y
    );
    const sz1 = Math.min(
      (PdfSize.A4.width - 2 * margin) / result.width,
      (PdfSize.A4.height - 2 * margin) / result.height
    );

    const { origin } = calcOrigin(c.x, c.y, margin, PdfSize.A4);
    const { origin: origin1 } = calcOrigin(result.width, result.height, margin, PdfSize.A4);

    // Pick random color palette
    const colorPalettes = [
      ['#ceb6e0', '#baf5e8'],
      ['#c5e2e3', '#fed1b6'],
      ['#5c557e', '#e5d5e8'],
      ['#e88dad', '#efe970'],
    ];
    const paletteIdx = opts.data.random.next(0, colorPalettes.length);
    const colors = colorPalettes[paletteIdx].map((h) => PdfColor.fromHex(h));

    // Direction options (8 directions, excluding center)
    const allOpts: C[] = [];
    for (let y = -1; y <= 1; y++) {
      for (let x = -1; x <= 1; x++) {
        if (x !== 0 || y !== 0) {
          allOpts.push(new C(x, y));
        }
      }
    }

    const count = result.path.length + 1;
    const backgrounds = [
      new PdfBrush(PdfColor.fromRgb(230, 230, 230)),
      new PdfBrush(PdfColor.fromRgb(204, 204, 204)),
    ];

    // Page 1: Solution grid
    const page0 = pdf.addPage();

    for (let y = 0; y < result.height; y++) {
      for (let x = 0; x < result.width; x++) {
        const rect = new PdfRect(origin1.x + x * sz1, origin1.y + y * sz1, sz1, sz1);
        const card = result.solution[y][x];

        if (card > 0) {
          const b = this.backBrush(colors, new C(x, y), result.width, result.height);

          page0.drawRectangle(null, b, rect);
          page0.drawRectangle(GUtil.basePen, null, rect);

          const cardNum = card < 10 ? `0${card}` : `${card}`;
          page0.drawString(cardNum, GUtil.baseFont, PdfBrush.Black, null, rect, ETextAlign.Center);
        }
      }
    }

    // Question card pages
    let currentPage: PageGen | null = null;

    for (let index = 0; index < count; index++) {
      const ii = index % (c.x * c.y);
      const cardY = Math.floor(ii / c.x);
      const cardX = ii % c.x;

      if (ii === 0) {
        currentPage = pdf.addPage();
      }
      const p = currentPage!;

      const rect0 = new PdfRect(origin.x + cardX * sz, origin.y + cardY * sz, sz, sz);

      const rect = new PdfRect(
        rect0.x + margin1,
        rect0.y + margin1,
        rect0.width - 2 * margin1,
        rect0.height - 2 * margin1
      );

      const r0 = new PdfRect(rect.x, rect.y, rect.width, (rect.height * 1) / 8);
      const r1 = new PdfRect(rect.x, r0.bottom, rect.width, (rect.height * 4) / 8);
      const r2 = new PdfRect(rect.x, r1.bottom, rect.width, (rect.height * 3) / 8);
      const r0x = new PdfRect(
        r0.x - margin1,
        r0.y - margin1,
        r0.width / 3 + margin1,
        r0.height + margin1
      );

      let b: PdfBrush;

      if (index === 0) {
        b = this.backBrush(colors, result.path[0].a, result.width, result.height);
      } else {
        b = this.backBrush(colors, result.path[index - 1].b, result.width, result.height);
      }

      p.drawRectangle(null, b, rect0);
      p.drawRectangle(null, PdfBrush.White, rect);
      p.drawRectangle(null, b, r0x);

      const indexNum = index + 1 < 10 ? `0${index + 1}` : `${index + 1}`;
      p.drawFillStringCenter(indexNum, GUtil.baseFont, PdfBrush.Black, r0x);

      p.drawRectangle(GUtil.cutPen, null, rect0);

      if (index === 0) {
        p.drawString(startLabel, GUtil.baseFont, PdfBrush.Black, null, rect0, ETextAlign.Center);
        continue;
      }

      const pathStep = result.path[index - 1];
      const n0 = result.cards.get(`${pathStep.a.x},${pathStep.a.y}`) ?? 0;

      // Generate question
      const gen = genMulti(opts.data, opts.generator, 1, false, false)[0];

      // Draw question text
      p.drawStringMulti(
        gen.texts.join('\n'),
        GUtil.baseFont,
        PdfBrush.Black,
        null,
        r1,
        ETextAlign.Center
      );

      // Draw answer options with direction indicators
      const answerW = r2.width / gen.answers.length;
      const correctDir = new C(-pathStep.b.x + pathStep.a.x, -pathStep.b.y + pathStep.a.y);

      // Get wrong direction options
      const wrongOptions = allOpts
        .filter((o) => o.x !== correctDir.x || o.y !== correctDir.y)
        .sort(() => opts.data.random.nextDouble() - 0.5)
        .slice(0, gen.answers.length);

      wrongOptions[gen.answerIndex] = correctDir;

      const answerFont = p.adjustFont(answerW, gen.answers, GUtil.baseFont);

      for (let i = 0; i < gen.answers.length; i++) {
        const rx = new PdfRect(r2.x + i * answerW, r2.y, answerW, r2.height);
        const r1x = new PdfRect(rx.x, rx.y, rx.width, rx.height / 3);
        const r2x = new PdfRect(
          rx.x + margin2,
          r1x.bottom + margin2,
          rx.width - 2 * margin2,
          (rx.height * 2) / 3 - 2 * margin2
        );

        const squareSize = Math.min(r2x.width, r2x.height);
        const rr = new PdfRect(
          r2x.x + (r2x.width - squareSize) / 2,
          r2x.y + (r2x.height - squareSize) / 2,
          squareSize,
          squareSize
        );

        p.drawRectangle(null, backgrounds[i % backgrounds.length], rx);
        p.drawFillStringCenter(gen.answers[i], answerFont, PdfBrush.Black, r1x);

        // Draw 3x3 direction grid
        const cellSize = rr.width / 3;
        const rr0 = new PdfRect(rr.x + cellSize, rr.y + cellSize, cellSize, cellSize);
        const rr1 = new PdfRect(
          rr.x + cellSize * (wrongOptions[i].x + 1),
          rr.y + cellSize * (wrongOptions[i].y + 1),
          cellSize,
          cellSize
        );

        p.drawRectangle(null, PdfBrush.Black, rr0);
        p.drawRectangle(GUtil.basePen, null, rr1);

        const n0Str = n0 < 10 ? `0${n0}` : `${n0}`;
        p.drawFillStringCenter(n0Str, GUtil.baseFont, PdfBrush.Black, rr1);
      }
    }
  }

  private backBrush(colors: PdfColor[], p: C, width: number, height: number): PdfBrush {
    const c0 = colors[0].getRgb();
    const c1 = colors[1].getRgb();
    const d = (p.y / (height - 1) - 0.5) * 40;
    const r = c0.r + ((c1.r - c0.r) / (width - 1)) * p.x + d;
    const g = c0.g + ((c1.g - c0.g) / (width - 1)) * p.x + d;
    const b = c0.b + ((c1.b - c0.b) / (width - 1)) * p.x + d;

    const minMax = (x: number) => Math.min(255, Math.max(0, Math.floor(x)));
    return new PdfBrush(PdfColor.fromRgb(minMax(r), minMax(g), minMax(b)));
  }

  private getPath(opts: OptsMulti): PathResult {
    const matrix = this.getMatrix();
    const height = matrix.length;
    const width = matrix[0].length;

    // Get all cells that are part of the shape
    const matrixSet = new Set<string>();
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (matrix[y][x] !== 0) {
          matrixSet.add(`${x},${y}`);
        }
      }
    }

    const path: PathStep[] = [];
    const doneSet = new Set<string>();
    const remainSet = new Set<string>(matrixSet);

    // Find starting point (cell with fewest neighbors)
    const posToNeighbors = new Map<string, C[]>();
    for (const key of remainSet) {
      const [x, y] = key.split(',').map(Number);
      posToNeighbors.set(key, this.getNear(remainSet, new C(x, y)));
    }

    let minNeighbors = Infinity;
    for (const [, neighbors] of posToNeighbors) {
      if (neighbors.length < minNeighbors) {
        minNeighbors = neighbors.length;
      }
    }

    const startCandidates: C[] = [];
    for (const [key, neighbors] of posToNeighbors) {
      if (neighbors.length === minNeighbors) {
        const [x, y] = key.split(',').map(Number);
        startCandidates.push(new C(x, y));
      }
    }

    const startIdx = opts.data.random.next(0, startCandidates.length);
    const start = startCandidates[startIdx];
    const startKey = `${start.x},${start.y}`;

    doneSet.add(startKey);
    remainSet.delete(startKey);

    let current = start;

    // Build path through all cells
    while (remainSet.size > 0) {
      const neighbors = this.getNear(remainSet, current);

      if (neighbors.length === 0) {
        // Backtrack: find a remaining cell adjacent to any done cell
        const searchable = [...remainSet]
          .map((k) => {
            const [x, y] = k.split(',').map(Number);
            return new C(x, y);
          })
          .sort(() => opts.data.random.next(0, 2) - 0.5);

        let found: C | null = null;
        for (const a of searchable) {
          for (const bKey of doneSet) {
            const [bx, by] = bKey.split(',').map(Number);
            if (this.areNear(a, new C(bx, by))) {
              found = new C(bx, by);
              break;
            }
          }
          if (found) break;
        }

        if (!found) throw new Error('Cannot find path through all tiles');
        current = found;
        continue;
      }

      // Pick random neighbor
      const pickIdx = opts.data.random.next(0, neighbors.length);
      const pick = neighbors[pickIdx];

      path.push({ a: current, b: pick });

      const pickKey = `${pick.x},${pick.y}`;
      remainSet.delete(pickKey);
      doneSet.add(pickKey);

      current = pick;
    }

    // Build card numbering
    const cards = new Map<string, number>();
    cards.set(`${start.x},${start.y}`, 1);

    for (let i = 0; i < path.length; i++) {
      cards.set(`${path[i].b.x},${path[i].b.y}`, i + 2);
    }

    // Build solution grid
    const solution: number[][] = [];
    for (let y = 0; y < height; y++) {
      solution[y] = [];
      for (let x = 0; x < width; x++) {
        solution[y][x] = cards.get(`${x},${y}`) ?? 0;
      }
    }

    return { width, height, cards, path, solution };
  }

  private getNear(remainSet: Set<string>, p: C): C[] {
    const result: C[] = [];
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const n = new C(p.x + dx, p.y + dy);
        if (remainSet.has(n.toHash())) {
          result.push(n);
        }
      }
    }
    return result;
  }

  private areNear(a: C, b: C): boolean {
    if (a.x === b.x && a.y === b.y) return false;
    return Math.abs(a.x - b.x) <= 1 && Math.abs(a.y - b.y) <= 1;
  }
}

// Tile Shape Variants

export class Dlazdice__Srdce extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [0, 1, 1, 0, 1, 1, 0],
      [1, 0, 0, 1, 0, 0, 1],
      [1, 0, 0, 0, 0, 0, 1],
      [0, 1, 0, 0, 0, 1, 0],
      [0, 0, 1, 0, 1, 0, 0],
      [0, 0, 0, 1, 0, 0, 0],
    ];
  }
}

export class Dlazdice__Dum extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [0, 0, 0, 1, 0, 0, 0],
      [0, 0, 1, 0, 1, 0, 0],
      [0, 1, 0, 0, 0, 1, 0],
      [1, 0, 0, 0, 0, 0, 1],
      [1, 1, 1, 1, 1, 1, 1],
      [1, 1, 0, 0, 0, 1, 1],
      [1, 0, 1, 0, 1, 0, 1],
      [1, 0, 0, 1, 0, 0, 1],
      [1, 0, 1, 0, 1, 0, 1],
      [1, 1, 0, 0, 0, 1, 1],
      [1, 1, 1, 1, 1, 1, 1],
    ];
  }
}

export class Dlazdice__Smajlik extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 0, 1, 1, 1, 1, 1, 0, 1, 1],
      [1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1],
      [0, 1, 1, 1, 0, 0, 0, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
    ];
  }
}

export class Dlazdice__T extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [1, 1, 1, 1],
      [1, 1, 1, 1],
      [0, 1, 1, 0],
      [0, 1, 1, 0],
      [0, 1, 1, 0],
    ];
  }
}

export class Dlazdice__Zamek extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [0, 1, 1, 0, 0, 0, 0, 0, 0],
      [1, 0, 0, 1, 0, 0, 0, 0, 0],
      [1, 0, 0, 1, 0, 0, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1],
      [0, 0, 1, 0, 0, 0, 0, 0, 1],
      [0, 0, 1, 0, 0, 0, 0, 0, 1],
      [0, 0, 1, 0, 0, 0, 0, 0, 1],
      [0, 0, 1, 1, 1, 1, 1, 1, 1],
    ];
  }
}

export class Dlazdice__Krouzek extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [1, 1, 1],
      [1, 0, 1],
      [1, 1, 1],
    ];
  }
}

export class Dlazdice__Lilie extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
      [0, 1, 1, 1, 0, 1, 1, 1, 1, 1, 0, 1, 1, 1, 0],
      [1, 1, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1, 1, 0, 1, 0, 1, 1, 0, 0, 0, 1],
      [0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
      [0, 0, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 0, 0],
      [0, 0, 1, 1, 1, 1, 0, 1, 0, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 0, 1, 1, 1, 0, 1, 1, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    ];
  }
}

export class Dlazdice__Mec extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [0, 0, 0, 0, 0, 0, 1, 1],
      [0, 0, 0, 0, 0, 1, 1, 1],
      [0, 0, 0, 0, 1, 1, 1, 0],
      [1, 0, 0, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 0, 0, 0],
      [0, 1, 1, 1, 0, 0, 0, 0],
      [1, 1, 1, 1, 0, 0, 0, 0],
      [1, 1, 0, 0, 1, 0, 0, 0],
    ];
  }
}

export class Dlazdice__Kafe extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [0, 0, 0, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 0, 0, 0, 0],
      [0, 0, 0, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 0, 0, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1],
      [0, 1, 0, 0, 0, 0, 1, 0, 1],
      [0, 1, 0, 0, 0, 0, 1, 0, 1],
      [0, 1, 0, 0, 0, 0, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 0, 0, 0],
      [1, 1, 1, 1, 1, 1, 1, 1, 0],
    ];
  }
}

export class Dlazdice__Vrtulnik extends DlazdiceBase {
  getMatrix(): number[][] {
    return [
      [0, 0, 0, 0, 1, 0, 0, 0, 0],
      [1, 1, 1, 1, 0, 1, 1, 1, 1],
      [0, 0, 0, 0, 1, 0, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 0, 0, 0],
      [0, 1, 1, 0, 1, 1, 0, 0, 1],
      [0, 1, 0, 0, 1, 1, 1, 1, 1],
      [0, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 1, 0, 0, 1, 0, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 0],
    ];
  }
}
